import { JOURNEY_STATUS } from "../../pages/DynamicTriggerEngine/AllJourneys/components/JourneyTable/utils/constants";
import { JOURNEY_TYPE } from "../../pages/DynamicTriggerEngine/CreateEditJourney/utils/constants";
import {
  AddNodeToJourneyPayload,
  DataType,
  PasteNodePayload,
  FieldOption,
  SearchJourneyNodesResponse,
  SearchJourneyNodesParams,
} from "../../pages/DynamicTriggerEngine/components/GraphBuilder/Nodes/utils/types";
import type {
  JourneyAnalyticsResponse,
  AnalyticsDonutSection,
  MetricEntitiesResponse,
  NodeSearchResponse,
  FunnelResultsPayload,
  FunnelViewResponse,
  SystemActionEntitiesResponse,
  SystemActionTaskEntity,
  SystemActionViewingEntity,
  ActivityOverTimeResponse,
  JourneyDurationResponse,
  ActiveHoursResponse,
  JourneyEntitiesResponse,
  LeadPathResponse,
} from "../../pages/DynamicTriggerEngine/CreateEditJourney/JourneyAnalytics/utils/types";
import { JourneyDetails } from "../slices/dynamicTriggerSlice";
import type {
  ActivityLogResponse,
  ActivityLogParams,
} from "../../pages/DynamicTriggerEngine/CreateEditJourney/JourneyActivityLog/utils/types";
import type { JourneysOverviewResponse } from "../../pages/DynamicTriggerEngine/AllJourneys/utils/types";
import type { JourneyHealthResponse } from "../../pages/DynamicTriggerEngine/CreateEditJourney/JourneyAnalytics/components/HealthSection/types/healthSection.types";
import { baseApi } from "./baseApi";

export const dynamicTriggerEngineApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllJourneys: builder.query({
      query: ({
        filters,
      }: {
        filters: {
          page: number;
          limit: number;
          tags?: string[];
          search?: string;
          statuses?: string[];
          sort_by?: string;
          sort_order?: string;
        };
      }) => {
        let queryParams = `?page=${filters.page}&limit=${filters.limit}`;
        if (filters.tags && filters.tags.length > 0) {
          queryParams += `&tags=${filters.tags.join(",")}`;
        }
        if (filters.search) {
          queryParams += `&search=${filters.search}`;
        }
        if (filters.statuses && filters.statuses.length > 0) {
          queryParams += `&statuses=${filters.statuses.join(",")}`;
        }
        if (filters.sort_by) {
          queryParams += `&sortBy=${filters.sort_by}`;
        }
        if (filters.sort_order) {
          queryParams += `&sortOrder=${filters.sort_order}`;
        }

        return {
          url: `/dynamic-trigger-engine/journey${queryParams}`,
          method: "GET",
        };
      },
      providesTags: [{ id: "journeys", type: "JourneyList" }],
    }),

    createJourney: builder.mutation({
      query: ({
        title,
        tags,
        journeyType,
        description,
      }: {
        title?: string;
        tags?: string[];
        journeyType: JOURNEY_TYPE;
        description?: string;
      }) => ({
        url: "/dynamic-trigger-engine/journey",
        method: "POST",
        body: { title, tags, journeyType, description },
      }),
    }),

    updateJourney: builder.mutation({
      query: (payload: {
        body: Partial<JourneyDetails>;
        journeyId: string;
      }) => ({
        url: `/dynamic-trigger-engine/journey/${payload.journeyId}`,
        method: "PATCH",
        body: payload.body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "JourneyDetails", id: arg.journeyId },
        { id: "journeys", type: "JourneyList" },
      ],
    }),
    getJourneyDetails: builder.query({
      query: ({
        journeyId,
        withAnalytics,
      }: {
        journeyId: string;
        withAnalytics?: boolean;
      }) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}/details${withAnalytics ? "?withAnalytics=true" : ""}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { journeyId }) => [
        { type: "JourneyDetails", id: journeyId },
      ],
    }),

    getWebhookUrl: builder.query({
      query: ({
        journeyId,
        webhookId,
      }: {
        journeyId: string;
        webhookId?: string;
      }) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}/webhook-url${webhookId ? `?webhookId=${webhookId}` : ""}`,
        method: "GET",
      }),
    }),

    addNodeToJourney: builder.mutation({
      query: (payload) => {
        return {
          url: "/dynamic-trigger-engine/journey/node",
          method: "POST",
          body: payload,
        };
      },
    }),

    pasteNode: builder.mutation({
      query: (payload: PasteNodePayload) => ({
        url: "/dynamic-trigger-engine/journey/node/paste",
        method: "POST",
        body: payload,
      }),
    }),

    updateNode: builder.mutation({
      query: (payload: {
        nodeId: string;
        body: Partial<AddNodeToJourneyPayload>;
      }) => {
        return {
          url: `/dynamic-trigger-engine/journey/node/${payload.nodeId}`,
          method: "PATCH",
          body: payload.body,
        };
      },
    }),

    deleteNode: builder.mutation({
      query: ({
        nodeId,
        journeyId,
      }: {
        nodeId: string;
        journeyId: string;
      }) => ({
        url: `/dynamic-trigger-engine/journey/node/${nodeId}`,
        method: "DELETE",
        body: {
          journeyId,
        },
      }),
    }),

    updateJourneyStatus: builder.mutation({
      query: ({
        journeyId,
        status,
      }: {
        journeyId: string;
        status: JOURNEY_STATUS;
      }) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}/status`,
        method: "PATCH",
        body: {
          status,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "JourneyDetails", id: arg.journeyId },
        { id: "journeys", type: "JourneyList" },
      ],
    }),

    deleteJourney: builder.mutation({
      query: (journeyId: string) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "JourneyDetails", id: arg },
        { id: "journeys", type: "JourneyList" },
      ],
    }),

    duplicateJourney: builder.mutation({
      query: ({
        journeyId,
        title,
        tags,
        description,
      }: {
        journeyId: string;
        title: string;
        description?: string;
        tags?: string[];
      }) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}/duplicate`,
        method: "POST",
        body: {
          title,
          ...(tags && tags.length > 0 && { tags }),
          ...(description && { description }),
        },
      }),
      invalidatesTags: () => [{ id: "journeys", type: "JourneyList" }],
    }),

    getAllJourneyTags: builder.query({
      query: () => ({
        url: "/dynamic-trigger-engine/journey/tags",
        method: "GET",
      }),
    }),

    getTemplateVariables: builder.query<
      Record<
        string,
        {
          key: string;
          displayValue: string;
          dataType: DataType;
          enumValues?: { value: string; label: string }[] | null;
          updatable?: boolean | null;
          optionsSource?: string | null;
          optionsPath?: string | null;
          dependsOn?: string | null;
        }[]
      >,
      void
    >({
      query: () => ({
        url: "/template-variables",
        method: "GET",
      }),
    }),

    /**
     * Fetch the selectable options for a remote enum field (e.g. destination
     * country/city/university). `optionsPath` comes straight from the field's
     * metadata; dependent fields additionally send their parent's selected
     * value(s) as `parent_value` (comma-separated when several are selected
     * across conditions). `limit` defaults to 500. The response is normalized
     * to `{ value, label }[]` regardless of the BE shape.
     */
    getFieldOptions: builder.query<
      FieldOption[],
      { optionsPath: string; parentValue?: string; limit?: number }
    >({
      query: ({ optionsPath, parentValue, limit = 500 }) => ({
        url: optionsPath,
        method: "GET",
        params: {
          limit,
          ...(parentValue ? { parent_value: parentValue } : {}),
        },
      }),
      transformResponse: (
        response:
          | string[]
          | FieldOption[]
          | { options?: (string | FieldOption)[] }
          | null,
      ): FieldOption[] => {
        const raw = Array.isArray(response) ? response : (response?.options ?? []);
        return raw.map((opt) =>
          typeof opt === "string" ? { value: opt, label: opt } : opt,
        );
      },
    }),

    /**
     * Journey activity log (vf-core proxy, enriched with actor names).
     * `actions` / `userIds` are forwarded as CSV; `dateFrom` / `dateTo` are
     * ISO-8601. Response is `{ data, pagination }`.
     */
    getJourneyActivityLog: builder.query<
      ActivityLogResponse,
      ActivityLogParams
    >({
      query: ({
        journeyId,
        page,
        limit,
        actions,
        userIds,
        dateFrom,
        dateTo,
        sortOrder,
      }) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}/activity-log`,
        method: "GET",
        params: {
          page,
          limit,
          ...(actions?.length ? { actions: actions.join(",") } : {}),
          ...(userIds?.length ? { userIds: userIds.join(",") } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
      }),
    }),

    getAIFlows: builder.query({
      query: () => ({
        url: "/client_config/flows",
        method: "GET",
      }),
    }),

    // Dashboard-wide totals for the All Journeys metrics strip. Note this is
    // `/analytics/overview` — NOT the per-journey `/analytics/journeys/{id}/overview` below.
    getJourneysOverview: builder.query<
      JourneysOverviewResponse,
      { range: string }
    >({
      query: ({ range }) => ({
        url: `/dynamic-trigger-engine/analytics/overview?range=${encodeURIComponent(range)}`,
        method: "GET",
      }),
    }),

    getJourneyAnalytics: builder.query<
      JourneyAnalyticsResponse,
      { journeyId: string; startDate: string; endDate: string; _v?: number }
    >({
      query: ({ journeyId, startDate, endDate }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/overview?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [
        { type: "JourneyAnalytics", id: arg.journeyId },
      ],
    }),

    getMetricEntities: builder.query<
      MetricEntitiesResponse,
      {
        journeyId: string;
        metricKey: string;
        startDate: string;
        endDate: string;
        page: number;
        limit: number;
        search?: string;
      }
    >({
      query: ({
        journeyId,
        metricKey,
        startDate,
        endDate,
        page,
        limit,
        search,
      }) => {
        let url = `/dynamic-trigger-engine/analytics/journeys/${journeyId}/metric-cards/${metricKey}/entities?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&page=${page}&limit=${limit}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url, method: "GET" };
      },
    }),

    // Backs both the Leads tab and the terminal-node drill-down, which narrows the same list
    // to one node via `currentNodeId`. `sort`/`order` are fixed at the API's defaults — the
    // design has no sort affordance on these headers.
    getJourneyEntities: builder.query<
      JourneyEntitiesResponse,
      {
        journeyId: string;
        startDate: string;
        endDate: string;
        page: number;
        limit: number;
        status?: string;
        search?: string;
        currentNodeId?: string;
      }
    >({
      query: ({
        journeyId,
        startDate,
        endDate,
        page,
        limit,
        status,
        search,
        currentNodeId,
      }) => {
        let url = `/dynamic-trigger-engine/analytics/journeys/${journeyId}/entities?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&page=${page}&limit=${limit}&sort=enteredAt&order=desc`;
        if (status) url += `&status=${encodeURIComponent(status)}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (currentNodeId)
          url += `&currentNodeId=${encodeURIComponent(currentNodeId)}`;
        return { url, method: "GET" };
      },
    }),

    // Leads that ended the journey at one terminal node — the Overview drill-down. Same envelope as
    // `getJourneyEntities` above, but its own route rather than that one narrowed by `currentNodeId`.
    // `range=custom` + explicit dates is how the other analytics endpoints send the page's picker.
    getTerminalDistributionEntities: builder.query<
      JourneyEntitiesResponse,
      {
        journeyId: string;
        nodeId: string;
        startDate: string;
        endDate: string;
        page: number;
        limit: number;
      }
    >({
      query: ({ journeyId, nodeId, startDate, endDate, page, limit }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/terminal-distribution/${nodeId}/entities?range=custom&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&page=${page}&limit=${limit}&sort=enteredAt&order=desc`,
        method: "GET",
      }),
    }),

    // One lead's route through one journey, for the Leads tab's "View path" modal. Entity-first path,
    // unlike its journey-first neighbours — that's the backend's shape, kept verbatim.
    getLeadJourneyPath: builder.query<
      LeadPathResponse,
      { entityId: string; journeyId: string }
    >({
      query: ({ entityId, journeyId }) => ({
        url: `/dynamic-trigger-engine/analytics/entities/${entityId}/journeys/${journeyId}/path`,
        method: "GET",
      }),
    }),

    getCreatedTasks: builder.query<
      SystemActionEntitiesResponse<SystemActionTaskEntity>,
      {
        journeyId: string;
        page: number;
        limit: number;
        search?: string;
        status?: string;
      }
    >({
      query: ({ journeyId, page, limit, search, status }) => {
        let url = `/dynamic-trigger-engine/analytics/journeys/${journeyId}/created-tasks?page=${page}&limit=${limit}`;
        if (status) url += `&status=${encodeURIComponent(status)}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url, method: "GET" };
      },
    }),

    getCreatedViewings: builder.query<
      SystemActionEntitiesResponse<SystemActionViewingEntity>,
      {
        journeyId: string;
        page: number;
        limit: number;
        search?: string;
        status?: string;
      }
    >({
      query: ({ journeyId, page, limit, search, status }) => {
        let url = `/dynamic-trigger-engine/analytics/journeys/${journeyId}/created-viewings?page=${page}&limit=${limit}`;
        if (status) url += `&status=${encodeURIComponent(status)}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url, method: "GET" };
      },
    }),

    getLostReasonDistribution: builder.query<
      AnalyticsDonutSection,
      {
        journeyId: string;
        startDate: string;
        endDate: string;
        primary?: string;
        _v?: number;
      }
    >({
      query: ({ journeyId, startDate, endDate, primary }) => {
        let url = `/dynamic-trigger-engine/analytics/journeys/${journeyId}/lost-reasons?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
        if (primary) url += `&primary=${encodeURIComponent(primary)}`;
        return { url, method: "GET" };
      },
    }),
    searchJourneyNodes: builder.query<
      SearchJourneyNodesResponse,
      SearchJourneyNodesParams
    >({
      query: ({ journeyId, ...params }) => ({
        url: `/dynamic-trigger-engine/journey/${journeyId}/nodes/search`,
        method: "GET",
        params,
      }),
    }),

    // The Health tab's blended score for the page's date window. No `range=custom` here — this
    // route takes the two dates on their own, unlike its Trends neighbours below.
    getJourneyHealthScore: builder.query<
      JourneyHealthResponse,
      { journeyId: string; startDate: string; endDate: string }
    >({
      query: ({ journeyId, startDate, endDate }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/health-score?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
      }),
    }),

    // The three Trends cards. All take the page's date window in the same `range=custom`
    // form `getFunnelView` uses.
    getJourneyTimeseries: builder.query<
      ActivityOverTimeResponse,
      { journeyId: string; startDate: string; endDate: string }
    >({
      query: ({ journeyId, startDate, endDate }) => ({
        // `metrics` is required — omitting it returns only the default series.
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/timeseries?range=custom&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&bucket=day&metrics=entries,completions,failures`,
        method: "GET",
      }),
    }),

    getJourneyLatency: builder.query<
      JourneyDurationResponse,
      { journeyId: string; startDate: string; endDate: string }
    >({
      query: ({ journeyId, startDate, endDate }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/latency?range=custom&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
      }),
    }),

    getJourneyHeatmap: builder.query<
      ActiveHoursResponse,
      { journeyId: string; startDate: string; endDate: string }
    >({
      query: ({ journeyId, startDate, endDate }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/heatmap?range=custom&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
      }),
    }),

    getAnalyticsDocument: builder.query<
      Blob,
      {
        journeyId: string;
        format: "csv" | "xlsx";
        startDate: string;
        endDate: string;
      }
    >({
      query: ({ journeyId, format, startDate, endDate }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/export/overall_metrics?format=${format}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
        responseHandler: async (response: Response) => response.blob(),
        cache: "no-cache",
      }),
    }),
    getMetricTableDocument: builder.query<
      Blob,
      {
        journeyId: string;
        format: "csv" | "xlsx";
        startDate: string;
        endDate: string;
        cardKey: string;
      }
    >({
      query: ({ journeyId, format, startDate, endDate, cardKey }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/export/metric_card_drilldown?format=${format}&cardKey=${cardKey}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
        responseHandler: async (response: Response) => response.blob(),
        cache: "no-cache",
      }),
    }),

    getJourneyNodes: builder.query<
      NodeSearchResponse,
      {
        journeyId: string;
        isConversion?: boolean;
        predecessorNodes?: string;
        searchQuery?: string;
        limit: number;
        offset: number;
      }
    >({
      query: ({
        journeyId,
        isConversion,
        predecessorNodes,
        searchQuery,
        limit,
        offset,
      }) => {
        let url = `/dynamic-trigger-engine/journey/${journeyId}/nodes/search?limit=${limit}&offset=${offset}`;
        if (isConversion) url += `&isConversion=true`;
        if (predecessorNodes)
          url += `&predecessorNodes=${encodeURIComponent(predecessorNodes)}`;
        if (searchQuery)
          url += `&searchQuery=${encodeURIComponent(searchQuery)}`;
        return { url, method: "GET" };
      },
    }),

    getFunnelView: builder.query<
      FunnelViewResponse,
      {
        journeyId: string;
        startDate: string;
        endDate: string;
      }
    >({
      query: ({ journeyId, startDate, endDate }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/funnel-view?range=custom&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        method: "GET",
      }),
    }),

    createFunnelView: builder.mutation<
      FunnelViewResponse,
      { journeyId: string; body: FunnelResultsPayload }
    >({
      query: ({ journeyId, body }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/funnel-view`,
        method: "POST",
        body,
      }),
    }),

    updateFunnelView: builder.mutation<
      FunnelViewResponse,
      {
        journeyId: string;
        body: Partial<FunnelResultsPayload>;
      }
    >({
      query: ({ journeyId, body }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/funnel-view`,
        method: "PATCH",
        body,
      }),
    }),

    getNodeExportDocument: builder.query<
      Blob,
      {
        journeyId: string;
        nodeId: string;
        exportType: "csv" | "xlsx";
        nodeType: string;
      }
    >({
      query: ({ journeyId, nodeId, exportType, nodeType }) => ({
        url: `/dynamic-trigger-engine/analytics/journeys/${journeyId}/nodes/${nodeId}/export/${nodeType}?format=${exportType}`,
        method: "GET",
        responseHandler: async (response: Response) => response.blob(),
        cache: "no-cache",
      }),
    }),
  }),
});

export const {
  useGetAllJourneysQuery,
  useCreateJourneyMutation,
  useUpdateJourneyMutation,
  useGetJourneyDetailsQuery,
  useLazyGetJourneyDetailsQuery,
  useGetWebhookUrlQuery,
  useAddNodeToJourneyMutation,
  usePasteNodeMutation,
  useUpdateNodeMutation,
  useDeleteNodeMutation,
  useUpdateJourneyStatusMutation,
  useDeleteJourneyMutation,
  useDuplicateJourneyMutation,
  useGetAllJourneyTagsQuery,
  useGetTemplateVariablesQuery,
  useGetFieldOptionsQuery,
  useGetJourneyActivityLogQuery,
  useGetAIFlowsQuery,
  useSearchJourneyNodesQuery,
  useLazySearchJourneyNodesQuery,
  useGetJourneysOverviewQuery,
  useGetJourneyAnalyticsQuery,
  useGetMetricEntitiesQuery,
  useGetJourneyEntitiesQuery,
  useGetTerminalDistributionEntitiesQuery,
  useGetLeadJourneyPathQuery,
  useGetCreatedTasksQuery,
  useGetCreatedViewingsQuery,
  useGetLostReasonDistributionQuery,
  useGetJourneyHealthScoreQuery,
  useGetJourneyTimeseriesQuery,
  useGetJourneyLatencyQuery,
  useGetJourneyHeatmapQuery,
  useLazyGetAnalyticsDocumentQuery,
  useLazyGetMetricTableDocumentQuery,
  useLazyGetJourneyNodesQuery,
  useLazyGetFunnelViewQuery,
  useCreateFunnelViewMutation,
  useUpdateFunnelViewMutation,
  useLazyGetNodeExportDocumentQuery,
} = dynamicTriggerEngineApi;
